import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type SortDirection, type v2 } from '@deque/billing-service-client';
import {
  Button,
  Icon,
  Link,
  Loader,
  PageHeader,
  Pagination,
  Panel,
  PanelContent,
  PanelHeader,
  Toast
} from '@deque/cauldron-react';
import { getPagination } from '@deque/pagination-utils';

import clamp from '~/common/utils/clamp';

import billingClient from '~/common/utils/billing-client/client-v2';
import useExportEnterpriseTeamsToCSV from '../hooks/useExportEnterpriseTeamsToCSV';
import useTeams from '../hooks/useTeams';

import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import DeleteTeamAlert from '../components/teams/DeleteTeamAlert';
import TeamSearch from '../components/teams/TeamSearch';
import TeamTable from '../components/teams/TeamTable';
import styles from './TeamList.css';

const PAGE_SIZE = 10;

export interface TeamsListProps {
  token: string;
  enterpriseId: string;
}

const TeamList: React.FC<TeamsListProps> = ({
  token,
  enterpriseId
}: TeamsListProps) => {
  const { t } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [nameQuery, setNameQuery] = useState<string>();

  const {
    isEnterpriseTeamsDataLoading,
    enterpriseTeamsDataError,
    processData,
    refreshEnterpriseTeamsData
  } = useExportEnterpriseTeamsToCSV();

  const [sort, setSort] = useState<[v2.TeamSortableColumn, SortDirection]>();

  const [error, setError] = useState<Error | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<v2.Team | null>(null);

  const [sortBy, sortDir] = sort || [];

  const {
    teams,
    loading,
    error: teamsError,
    refresh
  } = useTeams({
    token,
    enterpriseId,
    name: nameQuery,
    page,
    per_page: PAGE_SIZE,
    sortBy,
    sortDir
  });

  const pagination = useMemo(() => {
    const newPagination = teams && getPagination(teams);

    const totalPages = newPagination?.total_pages || 1;
    const gotoPage = (value: number) => setPage(clamp(value, 1, totalPages));

    return {
      currentPage: page,
      itemsPerPage: PAGE_SIZE,
      totalItems: newPagination?.total || teams?.length || 0,
      onNextPageClick: () => gotoPage(page + 1),
      onPreviousPageClick: () => gotoPage(page - 1),
      onFirstPageClick: () => gotoPage(1),
      onLastPageClick: () => gotoPage(totalPages)
    };
  }, [page, teams]);

  const onNameQueryChange = (query?: string) => {
    setNameQuery(query);
    setPage(1);
  };

  const openAlert = (team: v2.Team) => {
    setTeamToDelete(team);
    setShowAlert(true);
  };

  const onCancel = () => {
    setTeamToDelete(null);
    setShowAlert(false);
  };

  const onDelete = async () => {
    setError(null);
    setDeleting(true);
    setShowAlert(false);
    try {
      if (teamToDelete) {
        await billingClient.teams.delete({
          token,
          enterpriseId,
          id: teamToDelete.id
        });
        refresh();
        refreshEnterpriseTeamsData();
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setTeamToDelete(null);
      setDeleting(false);
    }
  };

  const onExport = useCallback(() => {
    const csvContent = processData();

    const today = new Date();

    // Create blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    // Create download link and click it
    const url = URL.createObjectURL(blob);
    const filename = `enterprise_teams_export_${today.toISOString().split('T')[0]}.csv`;
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [processData]);

  return (
    <div className={styles.container}>
      {(teamsError || error) && (
        <Toast show type="error">
          {teamsError?.message || error?.message}
        </Toast>
      )}
      {enterpriseTeamsDataError && (
        <Toast show type="error">
          {t('Failed to load enterprise teams for export.')}
        </Toast>
      )}
      {deleting && <ScrimmedLoader label={t('Deleting team...')} />}
      <PageHeader heading={t('Teams')} className={styles.header}>
        <div className={styles.headerActions}>
          <Link href="/teams/new" variant="button">
            <Icon type="plus" />
            {t('Create team')}
          </Link>
          <Button
            variant="secondary"
            disabled={
              isEnterpriseTeamsDataLoading || !!enterpriseTeamsDataError
            }
            onClick={onExport}
          >
            {t('Export to CSV')}
            <Icon type="export-solid" />
          </Button>
        </div>
      </PageHeader>
      <Panel>
        <PanelHeader>
          <TeamSearch query={nameQuery} setQuery={onNameQueryChange} />
        </PanelHeader>
        <PanelContent className={styles.tableContainer} aria-live="polite">
          {loading ? (
            <Loader tabIndex={-1} label={t('Loading teams')} />
          ) : teams && teams.length ? (
            <>
              <TeamTable
                teams={teams}
                sort={sort}
                deleteTeam={openAlert}
                setSort={setSort}
              />
              <Pagination className={styles.pagination} {...pagination} />
              <DeleteTeamAlert
                show={showAlert}
                team={teamToDelete}
                onDelete={onDelete}
                onCancel={onCancel}
              />
            </>
          ) : (
            <p className={styles.noMatchingTeams}>
              {t('No matching teams found')}
            </p>
          )}
        </PanelContent>
      </Panel>
    </div>
  );
};

export default TeamList;
